const SHELL_STYLES = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .widget-container {
    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 2147483647;
    --primary-color: #00D3F3;
    --primary-hover: #06B6D4;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease-in-out;
  }
  .widget-container.loaded { opacity: 1; visibility: visible; }

  .toggle-button {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: none;
    background: var(--primary-color);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s;
  }
  .toggle-button:hover { transform: scale(1.1); background: var(--primary-hover); }
  .toggle-button:active { transform: scale(0.95); }
  .toggle-button svg { transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }

  .welcome-card {
    position: absolute;
    bottom: 70px;
    right: 0;
    width: 280px;
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    animation: wc-slide-up 0.25s ease-out;
  }
  @keyframes wc-slide-up {
    from { transform: translateY(10px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  .welcome-card-header {
    background: var(--primary-color);
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px;
  }
  .welcome-card-title { font-size: 11px; color: rgba(255,255,255,0.85); font-weight: 500; margin: 0; padding-left: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .welcome-card-ai { font-size: 9px; color: rgba(255,255,255,0.9); border: 1px solid rgba(255,255,255,0.5); border-radius: 8px; padding: 1px 6px; margin-left: 6px; margin-right: auto; white-space: nowrap; }
  .welcome-card-close {
    width: 28px; height: 28px;
    border-radius: 50%; border: none;
    background: transparent; color: white;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background-color 0.2s;
  }
  .welcome-card-close:hover { background: rgba(255, 255, 255, 0.2); }
  .welcome-card-body {
    padding: 16px 20px; cursor: pointer;
    transition: background-color 0.2s;
  }
  .welcome-card-body:hover { background: #f9f9f9; }
  .welcome-card-text { font-size: 14px; line-height: 1.5; color: #333; margin: 0; text-align: left; }

  @media (max-width: 540px) {
    .widget-container { bottom: 12px; right: 12px; }
    .welcome-card { width: calc(100vw - 24px); max-width: 280px; }
  }
`;

const CHAT_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>`;
const CLOSE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;

export class DabotWidget extends HTMLElement {
  static observedAttributes = ['widget-id', 'lang', 'preview'];

  constructor() {
    super();
    this._shadow = this.attachShadow({ mode: 'open' });
    this._config = null;
    this._labels = {};
    this._currentLang = 'de';
    this._customer = '';
    this._apiEndpoint = '';
    this._isOpen = false;
    this._showWelcomeBox = false;
    this._welcomeCard = null;
    this._chatMounted = null;
    this._mountChatPanel = null;
    this._container = null;
    this._toggleBtn = null;
    this._connected = false;
  }

  connectedCallback() {
    this._connected = true;
    this._render();
    if (this.getAttribute('widget-id')) {
      this._loadConfig();
    }
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (!this._connected) return;
    if (name === 'widget-id' && newVal !== oldVal && newVal) {
      this._reset();
      this._loadConfig();
    }
  }

  _render() {
    const style = document.createElement('style');
    style.textContent = SHELL_STYLES;
    this._shadow.appendChild(style);

    const container = document.createElement('div');
    container.className = 'widget-container';
    this._shadow.appendChild(container);
    this._container = container;

    const toggleBtn = document.createElement('button');
    toggleBtn.className = 'toggle-button';
    toggleBtn.setAttribute('aria-label', 'Open chat');
    toggleBtn.innerHTML = CHAT_ICON;
    toggleBtn.addEventListener('click', () => this._toggle());
    container.appendChild(toggleBtn);
    this._toggleBtn = toggleBtn;

    this._shadow.addEventListener('keydown', (e) => {
      if (e.key === ' ' && e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'INPUT') {
        e.preventDefault();
      }
    });
  }

  _getApiBase() {
    const scripts = document.querySelectorAll('script[src*="dabot-widget"]');
    if (scripts.length > 0) {
      const url = new URL(scripts[0].src);
      return url.origin.replace('widget.', 'api.') + '/api/v1';
    }
    return 'https://api.dabot.ddev.site/api/v1';
  }

  async _loadConfig() {
    const widgetId = this.getAttribute('widget-id');
    if (!widgetId) return;
    try {
      const lang = this.getAttribute('lang');
      const urlParams = new URLSearchParams(window.location.search);
      const langParam = lang || urlParams.get('lang');
      const supportedLangs = ['de', 'en'];
      const browserLang = navigator.language || '';
      const browserLangCode = browserLang.toLowerCase().startsWith('de') ? 'de' : 'en';
      this._currentLang = langParam && supportedLangs.includes(langParam) ? langParam : browserLangCode;

      const apiBase = this._getApiBase();
      const res = await fetch(`${apiBase}/widgetid/${widgetId}`);
      if (!res.ok) throw new Error('Failed to load config');

      this._config = await res.json();
      this._customer = this._config.customer;
      this._apiEndpoint = this._config.api_endpoint || apiBase;
      this._labels = this._config.frontend_labels?.[this._currentLang]
        || this._config.frontend_labels?.de
        || {};

      // Art. 50 EU AI Act: KI-Hinweis mit Plattform-Default, kann per
      // Konfiguration umformuliert, aber nicht entfernt werden.
      if (!(this._labels.ai_disclosure || '').trim()) {
        this._labels.ai_disclosure = this._currentLang === 'de' ? 'KI-Assistent' : 'AI assistant';
      }

      const primaryColor = this._config.primary_color || '#00D3F3';
      const primaryHover = this._config.primary_color_hover || '#06B6D4';
      this._container.style.setProperty('--primary-color', primaryColor);
      this._container.style.setProperty('--primary-hover', primaryHover);
      // Optional per-customer color for the required-field asterisk. Falls back
      // to the primary color when not configured (e.g. qvest keeps its orange).
      const requiredMarkerColor = this._config.lead_form?.required_marker_color;
      if (requiredMarkerColor) {
        this._container.style.setProperty('--required-marker-color', requiredMarkerColor);
      }
      this._container.classList.add('loaded');

      const welcomeNote = this._labels.widget_welcome_box_note;
      if (welcomeNote?.trim() && !this._isWelcomeBoxDismissed()) {
        this._showWelcomeBox = true;
        this._renderWelcomeCard();
      }
    } catch (e) {
      console.error('Failed to load config:', e);
    }
  }

  _isWelcomeBoxDismissed() {
    if (!this._customer) return false;
    return sessionStorage.getItem(`${this._customer}_welcome_box_dismissed`) === 'true';
  }

  _renderWelcomeCard() {
    if (this._welcomeCard) this._welcomeCard.remove();
    const card = document.createElement('div');
    card.className = 'welcome-card';
    card.innerHTML = `
      <div class="welcome-card-header">
        <span class="welcome-card-title">${this._escapeHtml(this._labels.customer_title || '')}</span>
        <span class="welcome-card-ai">${this._escapeHtml(this._labels.ai_disclosure || '')}</span>
        <button class="welcome-card-close" aria-label="Close">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <div class="welcome-card-body" role="button" tabindex="0">
        <p class="welcome-card-text">${this._escapeHtml(this._labels.widget_welcome_box_note)}</p>
      </div>
    `;
    card.querySelector('.welcome-card-close').addEventListener('click', (e) => {
      e.stopPropagation();
      this._dismissWelcomeCard();
    });
    card.querySelector('.welcome-card-body').addEventListener('click', () => this._toggle());
    card.querySelector('.welcome-card-body').addEventListener('keypress', (e) => {
      if (e.key === 'Enter') this._toggle();
    });
    this._container.insertBefore(card, this._toggleBtn);
    this._welcomeCard = card;
  }

  _escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  _dismissWelcomeCard() {
    if (this._customer) {
      sessionStorage.setItem(`${this._customer}_welcome_box_dismissed`, 'true');
    }
    this._welcomeCard?.remove();
    this._welcomeCard = null;
    this._showWelcomeBox = false;
  }

  _toggle() {
    if (this._isOpen) {
      this._closeChat();
    } else {
      this._openChat();
    }
  }

  async _openChat() {
    if (this._showWelcomeBox) {
      this._welcomeCard?.remove();
      this._welcomeCard = null;
    }
    this._toggleBtn.innerHTML = CLOSE_ICON;
    this._toggleBtn.setAttribute('aria-label', 'Close chat');

    if (!this._mountChatPanel) {
      const { default: mountChatPanel } = await import('./mountChatPanel.js');
      this._mountChatPanel = mountChatPanel;
    }

    this._chatMounted = this._mountChatPanel(
      this._container,
      {
        widgetId: this.getAttribute('widget-id') || '',
        lang: this.getAttribute('lang') || '',
        preview: this.hasAttribute('preview'),
        config: this._config,
        labels: this._labels,
        currentLang: this._currentLang,
        customer: this._customer,
        apiEndpoint: this._apiEndpoint,
      },
      () => this._closeChat()
    );

    this._isOpen = true;
  }

  _closeChat() {
    if (this._chatMounted) {
      this._chatMounted.destroy();
      this._chatMounted = null;
    }
    this._toggleBtn.innerHTML = CHAT_ICON;
    this._toggleBtn.setAttribute('aria-label', 'Open chat');
    this._isOpen = false;
  }

  _reset() {
    this._closeChat();
    this._config = null;
    this._labels = {};
    this._customer = '';
    this._apiEndpoint = '';
    this._showWelcomeBox = false;
    this._welcomeCard?.remove();
    this._welcomeCard = null;
    this._container?.classList.remove('loaded');
  }
}
